import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Veltro PDP glue.
 *
 * 1. Keeps the "BYD model" variant in step with the shopper's saved car
 *    (assets/veltro-vehicle.js) in both directions:
 *      saved car  -> pre-select the matching variant radio / dropdown option
 *      picked variant -> save that car, so the rest of the store follows.
 * 2. Re-paints fitment state after Horizon morphs the section on variant change.
 * 3. <veltro-quick-add>: one-tap add for the "Complete the set" cards, routed
 *    through Horizon's CartLinesUpdateEvent so the drawer opens / refreshes.
 */

const OPTION_NAME = 'BYD model';
const stripYears = (s) => (s || '').replace(/\s*\(.*?\)\s*$/, '').trim();

/** handle -> label and label -> handle, from the fitment block's data-veltro-model-map. */
const modelMap = () => {
  const el = document.querySelector('[data-veltro-model-map]');
  const byHandle = {};
  const byLabel = {};
  if (el) {
    el.dataset.veltroModelMap.split(';').forEach((pair) => {
      const [h, l] = pair.split('|');
      if (h && l) {
        byHandle[h.trim()] = l.trim();
        byLabel[l.trim()] = h.trim();
      }
    });
  }
  return { byHandle, byLabel };
};

let urlVariantRespected = false;

/** Saved car -> variant. */
const selectVariantForVehicle = () => {
  const vehicle = window.VeltroVehicle?.get();
  if (!vehicle) return;
  // First paint only: a deep link with ?variant= wins over the remembered car.
  if (!urlVariantRespected) {
    urlVariantRespected = true;
    if (new URLSearchParams(location.search).has('variant')) return;
  }
  const radios = document.querySelectorAll(`variant-picker input[type="radio"][data-option-name="${OPTION_NAME}"]`);
  for (const input of radios) {
    const text = input.closest('label')?.querySelector('[data-key="variant-option-text"]')?.textContent || input.value;
    if (stripYears(text) === vehicle.label) {
      if (!input.checked && input.dataset.optionAvailable !== 'false') input.click();
      return;
    }
  }
  const selects = document.querySelectorAll('variant-picker select.variant-option__select');
  for (const sel of selects) {
    const opt = [...sel.options].find((o) => o.dataset.optionName === OPTION_NAME && stripYears(o.textContent) === vehicle.label);
    if (opt && !opt.selected) {
      sel.value = opt.value;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
  }
};

/** Picked variant -> saved car (only when it names a model we know). */
const onVariantChanged = (e) => {
  const input = e.target;
  if (!(input instanceof HTMLInputElement) || input.dataset.optionName !== OPTION_NAME || !input.checked) return;
  const text = input.closest('label')?.querySelector('[data-key="variant-option-text"]')?.textContent || input.value;
  const label = stripYears(text);
  const handle = modelMap().byLabel[label];
  const current = window.VeltroVehicle?.get();
  if (handle && (!current || current.handle !== handle)) window.VeltroVehicle.set(handle, label);
};

/** Pre-select the saved car inside "Complete the set" dropdowns. */
const syncSetSelects = () => {
  const vehicle = window.VeltroVehicle?.get();
  document.querySelectorAll('select[data-veltro-variant-select]').forEach((sel) => {
    if (vehicle) {
      const opt = [...sel.options].find((o) => stripYears(o.textContent).startsWith(vehicle.label));
      if (opt) sel.value = opt.value;
    }
    sel.dispatchEvent(new Event('change'));
  });
  document.querySelectorAll('.vset').forEach((section) => {
    const cards = [...section.querySelectorAll('.vset__card')];
    const visible = cards.filter((c) => c.dataset.veltroFitState !== 'no-fit');
    section.toggleAttribute('data-veltro-all-hidden', cards.length > 0 && visible.length === 0);
  });
};

const syncAll = () => {
  window.VeltroVehicle?.paint?.();
  selectVariantForVehicle();
  syncSetSelects();
};

document.addEventListener('change', onVariantChanged);
document.addEventListener('veltro:vehicle:change', () => {
  selectVariantForVehicle();
  syncSetSelects();
});

// Horizon re-renders the product section on variant change; repaint afterwards.
let morphTimer = null;
const section = document.querySelector('.shopify-section:has(product-form-component), [id^="shopify-section-"]:has(variant-picker)');
if (section) {
  new MutationObserver(() => {
    clearTimeout(morphTimer);
    morphTimer = setTimeout(() => {
      window.VeltroVehicle?.paint?.();
      syncSetSelects();
    }, 60);
  }).observe(section, { childList: true, subtree: true });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncAll);
else syncAll();

class VeltroQuickAdd extends Component {
  requiredRefs = ['button'];
  #select = null;
  #price = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.button.addEventListener('click', this.#onClick);
    const card = this.closest('.vset__card');
    this.#select = card?.querySelector('[data-veltro-variant-select]') ?? null;
    this.#price = card?.querySelector('[data-veltro-price]') ?? null;
    this.#select?.addEventListener('change', this.#onVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.button.removeEventListener('click', this.#onClick);
    this.#select?.removeEventListener('change', this.#onVariantChange);
  }

  #onVariantChange = () => {
    const price = this.#select?.selectedOptions?.[0]?.dataset.price;
    if (price && this.#price) this.#price.textContent = price;
  };

  #onClick = async () => {
    const variantId = this.#select?.value || this.dataset.variantId;
    if (!variantId || this.hasAttribute('data-busy')) return;
    this.setAttribute('data-busy', '');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
      const result = await response.json();
      if (result.status) {
        this.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID', detail: { description: result.description, errors: result.errors } }));
        deferred.reject(new Error(result.description || result.message));
        this.removeAttribute('data-busy');
        return;
      }
      deferred.resolve(result);
      this.setAttribute('data-added', '');
      setTimeout(() => this.removeAttribute('data-added'), 1800);
    } catch (error) {
      deferred.reject(error);
    } finally {
      this.removeAttribute('data-busy');
    }
  };
}

if (!customElements.get('veltro-quick-add')) customElements.define('veltro-quick-add', VeltroQuickAdd);
